import { useCallback, useMemo } from 'react';
import AuthFallback from '@components/auth/AuthFallback';
import { useApp } from '@context/AppContext';
import { useVendor } from '@context/VendorContext';
import { OnboardingWidget } from '@features/onboarding';
import { FlexView } from '@goodfynd/ui';
import { LoadingTruck } from '@goodfynd/ui';
import Box from '@mui/material/Box';
import NoSsr from '@mui/material/NoSsr';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { useRequireAuth } from '../../../hooks/useRequireAuth';
import { Sidebar, SidebarProvider } from '../Sidebar';
import { DashboardHeader } from '../Sidebar/dashboard-header/DashboardHeader';

import { StyledBody } from './styles';

export default function Layout({ children, fullWidth }: LayoutProps) {
  useRequireAuth();

  const { isLoggedIn, user, userIsLoading } = useApp();
  const { isLoading } = useVendor();
  const { isReady } = useRouter();

  const loadingComponent = useMemo(
    () => (
      <FlexView align="center" height="100vh" justify="center" size={1}>
        <LoadingTruck />
      </FlexView>
    ),
    []
  );
  const renderPage = useCallback(() => {
    if (isLoading || !isReady || userIsLoading) {
      return loadingComponent;
    }

    if (!isLoggedIn || !user?.id) {
      return <AuthFallback />;
    }

    return (
      <SidebarProvider>
        <FlexView wrap="nowrap">
          <Sidebar />

          <Box
            component="main"
            sx={{
              height: '100%',
              paddingBottom: 16,
              position: 'relative',
              maxWidth: '100%',
              width: {
                sm: fullWidth
                  ? 'fit-content'
                  : `calc(1440px - var(--sidebar-width))`,
                xs: '100%',
              },
            }}
          >
            <DashboardHeader />

            <StyledBody>{children}</StyledBody>

            <OnboardingWidget />
          </Box>
        </FlexView>
      </SidebarProvider>
    );
  }, [
    children,
    fullWidth,
    isLoading,
    isLoggedIn,
    isReady,
    loadingComponent,
    user?.id,
    userIsLoading,
  ]);

  return (
    <>
      <Head>
        <meta content="noindex, nofollow" name="robots" />
      </Head>

      <NoSsr>{renderPage()}</NoSsr>
    </>
  );
}
